import { getActivePinia } from "pinia";
import { useAuthStore } from "@/stores/auth";
import { useToastStore } from "@/stores/toast";

type ImageGenToastOptions = {
  id?: string;
  autoCloseMs?: number;
};

/**
 * Уведомление при старте генерации изображения.
 *
 * GC-6: раньше тост уверенно говорил «Списан 1 geni» ещё ДО ответа бэка — это
 * обманывало юзера, если генерация фейлилась (Gemini PROHIBITED_CONTENT и т.п.).
 * Бэк refund-ит geni при failed → юзер ничего не платит. Меняем формулировку
 * на нейтральную «Генерация изображения…», а финальный «Списано N geni» (с
 * актуальным балансом) выдаём в pushImageGenGeniToast строго на success.
 */
export function pushImageGenWillSpendToast(geniToSpend = 1, opts: ImageGenToastOptions = {}): string | null {
  if (typeof window === "undefined" || geniToSpend <= 0) return null;
  if (!getActivePinia()) return null;
  const toastStore = useToastStore();
  return toastStore.push({
    id: opts.id,
    title: "Генерация изображения…",
    description: "Это займёт до минуты. Geni спишутся только при успешной генерации.",
    tone: "neutral",
    state: "loading",
    autoCloseMs: opts.autoCloseMs ?? 3200,
  });
}

/** Уведомление о списании geni после успешной генерации изображения на бэкенде. */
export function pushImageGenGeniToast(
  geniSpent: number,
  geniBalance?: number,
  opts: ImageGenToastOptions = {}
): string | null {
  if (typeof window === "undefined" || geniSpent <= 0) return null;
  if (!getActivePinia()) return null;
  const authStore = useAuthStore();
  const toastStore = useToastStore();
  if (typeof geniBalance === "number" && Number.isFinite(geniBalance)) {
    authStore.setGeniBalance(Math.max(0, geniBalance));
  } else {
    authStore.setGeniBalance(Math.max(0, authStore.geniBalance - geniSpent));
  }
  const title =
    geniSpent === 1 ? "Списан 1 geni за генерацию изображения" : `Списано ${geniSpent} geni за генерацию изображения`;
  const description =
    typeof geniBalance === "number" ? `Текущий баланс: ${geniBalance} geni` : undefined;
  return toastStore.push({
    id: opts.id,
    title,
    description,
    tone: "neutral",
    state: "done",
  });
}

export function pushInsufficientGeniForImageGenToast(opts: ImageGenToastOptions = {}): string | null {
  if (typeof window === "undefined") return null;
  if (!getActivePinia()) return null;
  const toastStore = useToastStore();
  return toastStore.push({
    id: opts.id,
    title: "Недостаточно geni на балансе",
    description: "Пополните баланс, чтобы продолжить генерацию изображения.",
    tone: "error",
    state: "done",
    autoCloseMs: opts.autoCloseMs ?? 5200,
  });
}

export function dismissImageGenToast(toastId?: string | null): void {
  if (!toastId || typeof window === "undefined") return;
  if (!getActivePinia()) return;
  useToastStore().dismiss(toastId);
}

/** Уведомление о списании geni при запуске генерации контента (по кол-ву фото). */
export function pushContentGenGeniToast(geniSpent: number, opts?: { geniBalance?: number; autoCloseMs?: number }): void {
  if (typeof window === "undefined" || geniSpent <= 0) return;
  if (!getActivePinia()) return;
  const authStore = useAuthStore();
  const toastStore = useToastStore();

  const geniBalance = opts?.geniBalance;
  if (typeof geniBalance === "number" && Number.isFinite(geniBalance)) {
    authStore.setGeniBalance(Math.max(0, geniBalance));
  } else {
    authStore.setGeniBalance(Math.max(0, authStore.geniBalance - geniSpent));
  }

  const title = geniSpent === 1 ? "Списан 1 geni" : `Списано ${geniSpent} geni`;
  const description =
    geniSpent === 1 ? "Оплата за 1 сгенерированное фото." : `Оплата за ${geniSpent} сгенерированных фото.`;

  toastStore.push({
    title,
    description,
    tone: "success",
    state: "done",
    autoCloseMs: opts?.autoCloseMs ?? 3200,
  });
}
